import React from 'react';
import {
  Alert,
  Card,
  Divider,
  Space,
} from 'antd';
import {
  CarOutlined,
  FileTextOutlined,
  InboxOutlined,
  ShoppingOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { formatCurrencyLocalized } from '../../../utils/currency';
import { formatDate } from '../../../utils/dateUtils';
import { formatUnit } from '../../../utils/units';
import { formatLocalizedPercent, localeForLanguage } from '../../../utils/numberFormatting';
import { OrderMarginExplanationDto } from '../api/ordersApi';
import { getOrderProductDisplayName } from '../orderProductDisplay';
import { hasRevenueForMarginPercent } from '../orderFinanceDisplay';
import './OrderMarginExplanationSection.css';
import {
  renderSectionTitle,
  SECTION_CARD_BODY_STYLE,
  SECTION_CARD_HEAD_STYLE,
  SECTION_CARD_STYLE,
} from '../orderDetailLayout';

interface OrderMarginExplanationSectionProps {
  marginExplanation: OrderMarginExplanationDto | null;
  loading: boolean;
}

export const OrderMarginExplanationSection: React.FC<OrderMarginExplanationSectionProps> = ({
  marginExplanation,
  loading,
}) => {
  const { t, i18n } = useTranslation(['orders', 'transport']);
  const formatMoney = (value: number | string | null | undefined) =>
    formatCurrencyLocalized(value, 'PLN', i18n.language);
  const lines = marginExplanation?.lines || [];
  const unassignedAllocations = marginExplanation?.unassignedAllocations || [];
  const supplierPurchaseCostPLN = marginExplanation?.totals.supplierPurchaseCostPLN || 0;
  const supplierLandedCostPLN = marginExplanation?.totals.supplierLandedCostPLN || 0;
  const supplierManualHistoricalCostPLN = marginExplanation?.totals.supplierManualHistoricalCostPLN || 0;
  const supplierAllocationRoundingAdjustmentPLN = marginExplanation?.totals.supplierAllocationRoundingAdjustmentPLN || 0;
  const orderLevelCostPLN = marginExplanation
    ? Number((marginExplanation.orderLevelCosts.cogsPLN + marginExplanation.orderLevelCosts.manualCostPLN).toFixed(2))
    : 0;
  const hasUnassignedSupplierCost = Boolean(
    marginExplanation && Math.abs(marginExplanation.totals.unassignedAllocationCostPLN) > 0.005
  );
  const unavailableValue = <span style={{ color: '#94a3b8' }}>—</span>;
  const costBuildUpRows = marginExplanation ? [
    {
      key: 'supplierPurchaseCost',
      label: t('orders:orderDetail.supplierPurchaseCostCategory'),
      description: t('orders:orderDetail.supplierPurchaseCostCategoryDescription'),
      value: supplierPurchaseCostPLN,
      icon: <ShoppingOutlined />,
      color: '#2563eb',
      background: '#eff6ff',
    },
    {
      key: 'supplierLandedCost',
      label: t('orders:orderDetail.supplierLandedCostCategory'),
      description: t('orders:orderDetail.supplierLandedCostCategoryDescription'),
      value: supplierLandedCostPLN,
      icon: <CarOutlined />,
      color: '#c2410c',
      background: '#fff7ed',
    },
    {
      key: 'supplierManualHistoricalCost',
      label: t('orders:orderDetail.supplierManualHistoricalCostCategory'),
      description: t('orders:orderDetail.supplierManualHistoricalCostCategoryDescription'),
      value: supplierManualHistoricalCostPLN,
      icon: <FileTextOutlined />,
      color: '#7c2d12',
      background: '#fff7ed',
    },
    {
      key: 'supplierAllocationRoundingAdjustment',
      label: t('orders:orderDetail.supplierAllocationRoundingAdjustmentCategory'),
      description: t('orders:orderDetail.supplierAllocationRoundingAdjustmentCategoryDescription'),
      value: supplierAllocationRoundingAdjustmentPLN,
      icon: <ShoppingOutlined />,
      color: '#9a3412',
      background: '#fff7ed',
    },
    {
      key: 'warehouseCost',
      label: t('orders:orderDetail.warehouseCostCategory'),
      description: t('orders:orderDetail.warehouseCostCategoryDescription'),
      value: marginExplanation.orderLevelCosts.cogsPLN,
      icon: <InboxOutlined />,
      color: '#0f766e',
      background: '#f0fdfa',
    },
    {
      key: 'transportCost',
      label: t('orders:orderDetail.transportCostCategory'),
      description: t('orders:orderDetail.transportCostCategoryDescription'),
      value: marginExplanation.orderLevelCosts.transportCostPLN,
      icon: <CarOutlined />,
      color: '#7c3aed',
      background: '#f5f3ff',
    },
    {
      key: 'otherOrderCosts',
      label: t('orders:orderDetail.otherOrderCostsCategory'),
      description: t('orders:orderDetail.otherOrderCostsCategoryDescription'),
      value: marginExplanation.orderLevelCosts.manualCustomerCostPLN,
      icon: <FileTextOutlined />,
      color: '#475569',
      background: '#f8fafc',
    },
    {
      key: 'productFallbackCost',
      label: t('orders:orderDetail.productFallbackCostCategory'),
      description: t('orders:orderDetail.productFallbackCostCategoryDescription'),
      value: marginExplanation.orderLevelCosts.productFallbackCostPLN,
      icon: <FileTextOutlined />,
      color: '#64748b',
      background: '#f8fafc',
    },
  ].filter((row) => Number(row.value || 0) !== 0) : [];
  const exceptionRows = marginExplanation ? [
    {
      key: 'unassignedAllocationCost',
      label: t('orders:orderDetail.unassignedAllocationCost'),
      value: marginExplanation.totals.unassignedAllocationCostPLN,
    },
    {
      key: 'unexplainedCost',
      label: t('orders:orderDetail.unexplainedCost'),
      value: marginExplanation.orderLevelCosts.unexplainedCostPLN,
    },
  ].filter((row) => Number(row.value || 0) !== 0) : [];
  const hasCostBuildUp = costBuildUpRows.length > 0 || exceptionRows.length > 0;
  const exchangeRateLabel = marginExplanation?.order.exchangeRate == null
    ? null
    : Number(marginExplanation.order.exchangeRate).toLocaleString(localeForLanguage(i18n.language), {
        minimumFractionDigits: 4,
        maximumFractionDigits: 6,
      });
  const exchangeRateSourceLabel = marginExplanation?.order.exchangeRateSource
    ? t(`transport:allocation.costProvenanceSources.${marginExplanation.order.exchangeRateSource}`, {
        defaultValue: marginExplanation.order.exchangeRateSource,
      })
    : null;

  return (
    <Card
      title={renderSectionTitle(t('orders:orderDetail.marginExplanation'))}
      style={SECTION_CARD_STYLE}
      styles={{ header: SECTION_CARD_HEAD_STYLE, body: SECTION_CARD_BODY_STYLE }}
      loading={loading}
    >
      {marginExplanation ? (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          {marginExplanation.order.exchangeRate && (
            <Alert
              type="info"
              showIcon
              message={t('orders:orderDetail.exchangeRateSnapshot')}
              description={[
                `${marginExplanation.order.currency} -> PLN: ${exchangeRateLabel}`,
                marginExplanation.order.exchangeRateEffectiveDate
                  ? `${t('orders:orderDetail.rateFrom')} ${formatDate(marginExplanation.order.exchangeRateEffectiveDate)}`
                  : null,
                exchangeRateSourceLabel,
                marginExplanation.order.exchangeRateTableNumber || null,
              ].filter(Boolean).join(' · ')}
            />
          )}
          {!marginExplanation.totals.reconciles && (
            <Alert
              type="warning"
              showIcon
              message={t('orders:orderDetail.marginExplanationUnreconciled')}
            />
          )}
          {hasUnassignedSupplierCost && (
            <Alert
              type="warning"
              showIcon
              message={t('orders:orderDetail.unassignedAllocationWarningTitle')}
              description={t('orders:orderDetail.unassignedAllocationWarningDescription')}
            />
          )}
          {hasUnassignedSupplierCost && (
            <div className="order-margin-unassigned-list">
              {unassignedAllocations.map((allocation) => (
                <div key={allocation.linkId || `${allocation.supplierOrderId}-${allocation.supplierOrderItemId}`}>
                  <strong>{allocation.supplierOrderNumber || t('orders:orderDetail.unknownSupplierOrder')}</strong>
                  <span>{allocation.supplierName || t('orders:orderDetail.unknownSupplier')}</span>
                  <span>{allocation.supplierOrderItemName || t('orders:orderDetail.wholeSupplierOrder')}</span>
                  <strong>{formatMoney(allocation.effectiveAllocatedCostPLN)}</strong>
                </div>
              ))}
            </div>
          )}
          {orderLevelCostPLN !== 0 && (
            <Alert
              type="info"
              showIcon
              message={t('orders:orderDetail.lineResultsExcludeOrderLevelCosts')}
            />
          )}
          {lines.length === 0 ? (
            <Alert type="info" message={t('orders:orderDetail.noMarginExplanationLines')} />
          ) : (
            <div className="order-margin-line-list">
              {lines.map((line) => (
                <article className="order-margin-line-card" key={line.id}>
                  <header>
                    <strong>{getOrderProductDisplayName(line, line.name)}</strong>
                    <span>{line.saleQuantity.toLocaleString(localeForLanguage(i18n.language))} {formatUnit(line.saleUnit, i18n.language)}</span>
                  </header>
                  <div className="order-margin-line-card__metrics">
                    {[
                      [t('orders:orderDetail.revenue'), formatMoney(line.revenuePLN)],
                      [t('orders:orderDetail.purchaseCost'), hasUnassignedSupplierCost ? unavailableValue : formatMoney(line.supplierPurchaseCostPLN)],
                      [t('orders:orderDetail.landedCost'), hasUnassignedSupplierCost ? unavailableValue : formatMoney(line.supplierLandedCostPLN)],
                      [t('orders:orderDetail.caseATotalCost'), hasUnassignedSupplierCost ? unavailableValue : formatMoney(line.allocatedCostPLN)],
                      [t('orders:orderDetail.attributedLineResultBeforeOrderCosts'), hasUnassignedSupplierCost
                        ? unavailableValue
                        : `${formatMoney(line.marginPLN)} (${hasRevenueForMarginPercent(line.revenuePLN) ? formatLocalizedPercent(line.marginPercent, i18n.language) : t('orders:orderDetail.marginPercentUnavailable')})`],
                    ].map(([label, value]) => (
                      <div key={String(label)}>
                        <span>{label}</span>
                        <strong>{value}</strong>
                      </div>
                    ))}
                  </div>
                  {line.allocations.length > 0 && (
                    <details className="order-margin-line-card__allocations">
                      <summary>{t('orders:orderDetail.supplierAllocationDetails', { count: line.allocations.length })}</summary>
                      {line.allocations.map((allocation) => (
                        <div key={allocation.linkId || `${allocation.supplierOrderId}-${allocation.supplierOrderItemId}`}>
                          <span>{allocation.supplierOrderNumber || '—'} · {allocation.supplierName || '—'}</span>
                          <span>{allocation.supplierOrderItemName || '—'}</span>
                          <strong>{formatMoney(allocation.effectiveAllocatedCostPLN)}</strong>
                        </div>
                      ))}
                    </details>
                  )}
                </article>
              ))}
            </div>
          )}
          {hasCostBuildUp && (
            <div
              style={{
                border: '1px solid #dbe4ee',
                borderRadius: 14,
                background: '#fbfdff',
                padding: 16,
              }}
            >
              <div style={{ marginBottom: 14 }}>
                <div style={{ color: '#0f172a', fontSize: 16, fontWeight: 750 }}>
                  {t('orders:orderDetail.marginBuildUpTitle')}
                </div>
                <div style={{ color: '#64748b', fontSize: 13, marginTop: 3 }}>
                  {t('orders:orderDetail.marginBuildUpDescription')}
                </div>
              </div>
              <div style={{ display: 'grid', gap: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'center', padding: '10px', borderRadius: 10, background: '#ecfdf5' }}>
                  <span style={{ color: '#047857', fontWeight: 750 }}>{t('orders:orderDetail.marginRevenueSection')}</span>
                  <strong style={{ color: '#047857' }}>{formatMoney(marginExplanation.totals.revenuePLN)}</strong>
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '8px 10px 2px' }}>
                  <span style={{ color: '#334155', fontWeight: 750 }}>{t('orders:orderDetail.marginCostsSection')}</span>
                  <span style={{ color: '#64748b', fontSize: 12 }}>{t('orders:orderDetail.marginCostsSectionDescription')}</span>
                </div>
                {costBuildUpRows.map((row) => (
                  <div
                    key={row.key}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 16,
                      alignItems: 'center',
                      padding: '10px',
                      borderRadius: 10,
                      background: row.background,
                    }}
                  >
                    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', minWidth: 0 }}>
                      <span style={{ color: row.color, fontSize: 16, lineHeight: 1.35 }}>{row.icon}</span>
                      <span style={{ minWidth: 0 }}>
                        <span style={{ display: 'block', color: '#334155', fontWeight: 650 }}>{row.label}</span>
                        <span style={{ display: 'block', color: '#64748b', fontSize: 12, lineHeight: 1.35 }}>{row.description}</span>
                      </span>
                    </div>
                    <strong style={{ color: '#0f172a', whiteSpace: 'nowrap' }}>
                      {Number(row.value || 0) >= 0 ? '- ' : '+ '}{formatMoney(Math.abs(Number(row.value || 0)))}
                    </strong>
                  </div>
                ))}
              </div>
              <Divider style={{ margin: '14px 0 10px' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'center' }}>
                <span style={{ color: '#475569', fontWeight: 700 }}>{t('orders:orderDetail.marginTotalCosts')}</span>
                <strong style={{ color: '#0f172a', fontSize: 16 }}>- {formatMoney(Math.abs(Number(marginExplanation.totals.totalCostPLN || 0)))}</strong>
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 16,
                  alignItems: 'center',
                  marginTop: 10,
                  padding: '12px 10px',
                  borderRadius: 10,
                  background: marginExplanation.totals.marginPLN >= 0 ? '#ecfdf5' : '#fef2f2',
                  color: marginExplanation.totals.marginPLN >= 0 ? '#047857' : '#b91c1c',
                }}
              >
                <span style={{ fontWeight: 750 }}>{t('orders:orderDetail.margin')}</span>
                <strong style={{ fontSize: 17 }}>
                  {formatMoney(marginExplanation.totals.marginPLN)} ({hasRevenueForMarginPercent(marginExplanation.totals.revenuePLN)
                    ? formatLocalizedPercent(marginExplanation.totals.marginPercent, i18n.language)
                    : t('orders:orderDetail.marginPercentUnavailable')})
                </strong>
              </div>
              {exceptionRows.length > 0 && (
                <div style={{ marginTop: 12, display: 'grid', gap: 4 }}>
                  {exceptionRows.map((row) => (
                    <span key={row.key} style={{ color: '#b45309', fontSize: 12 }}>
                      <WarningOutlined style={{ marginRight: 6 }} />
                      {row.label}: {formatMoney(row.value)}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </Space>
      ) : (
        <Alert type="info" showIcon message={t('orders:orderDetail.marginExplanationUnavailable')} />
      )}
    </Card>
  );
};
